import {MAX_REGEX_CAPTURE_GROUPS} from '../constants';

type CaptureGroupMetadata = {
  start: number;
  end: number;
  content: string;
};

export function validateMultiMetricQuickAddRegex(pattern: string): {
  errorMessage: string | null;
} {
  // Validate regex syntax
  try {
    new RegExp(pattern);
  } catch (error) {
    return {errorMessage: 'Invalid regex syntax.'};
  }

  // Validate no nested groups
  if (hasNestedGroups(pattern)) {
    return {errorMessage: 'Nested capture groups are not allowed.'};
  }

  // Validate num capture groups
  const captureGroups = extractCaptureGroups(pattern);
  if (captureGroups.length > MAX_REGEX_CAPTURE_GROUPS) {
    return {
      errorMessage: `Up to ${MAX_REGEX_CAPTURE_GROUPS} capture groups are allowed.`,
    };
  }

  // Validate no empty capture groups
  if (captureGroups.some(group => group.content === '')) {
    return {errorMessage: 'Empty capture groups are not allowed.'};
  }

  return {errorMessage: null};
}

/**
 * Given a metric and a regex pattern, return regex with captured values filled in.
 * Input regex pattern is expected to be pre-validated for multi-metric quick add.
 * Returns undefined if the metric does not match the regex.
 * @example
 *   getFilledRegex("foo/bar", "foo/(.+)") -> "foo/(bar)"
 *   getFilledRegex("foo/bar", "baz/bar") -> undefined
 */
export function getFilledRegex(
  metric: string,
  validatedPattern: string
): string | undefined {
  const regex = new RegExp(validatedPattern);
  const match = regex.exec(metric);

  if (!match) {
    return;
  }

  // Extract capture groups
  const captureGroups = extractCaptureGroups(validatedPattern);

  // If no capture groups, return pattern as-is
  if (captureGroups.length === 0) {
    return validatedPattern;
  }

  // Replace capture groups with captured values from right to left (preserves indices)
  let filledPattern = validatedPattern;
  for (let i = captureGroups.length - 1; i >= 0; i--) {
    const group = captureGroups[i];
    const capturedValue = match[i + 1];
    if (capturedValue !== undefined) {
      const before = filledPattern.substring(0, group.start);
      const after = filledPattern.substring(group.end);
      filledPattern = `${before}(${capturedValue})${after}`;
    }
  }
  return filledPattern;
}

/**
 * Checks whether a regex pattern contains nested groups.
 * @example
 *   hasNestedGroups("foo/(b(ar))/baz") -> true
 *   hasNestedGroups("foo/(bar)/baz") -> false
 */
export function hasNestedGroups(pattern: string): boolean {
  let isInGroup = false;

  for (let i = 0; i < pattern.length; i++) {
    const char = pattern[i];

    // Ignore escaped characters
    if (char === '\\') {
      i++; // skips the next character, which is escaped
      continue;
    }

    // Check for start of group
    if (char === '(') {
      // Ignore non-capturing groups
      const isNonCapturing = pattern[i + 1] === '?' && pattern[i + 2] === ':';
      if (isNonCapturing) {
        i += 2; // skip next two chars (?:)
        continue;
      }

      // Opening paren while already in a group -> nested group found
      if (isInGroup) {
        return true;
      }

      isInGroup = true;
    } else if (isInGroup && char === ')') {
      isInGroup = false;
    }
  }

  return false;
}

/**
 * Extracts capture group positions from a regex pattern.
 * Assumes no nested groups.
 * @example
 *   extractCaptureGroups("foo/(.+)/(baz)") => [
 *     {start: 4, end: 8, content: ".+"},
 *     {start: 9, end: 14, content: "baz"},
 *   ]
 */
export function extractCaptureGroups(pattern: string): CaptureGroupMetadata[] {
  const groups: CaptureGroupMetadata[] = [];

  for (let i = 0; i < pattern.length; i++) {
    const char = pattern[i];

    // Ignore escaped characters
    if (char === '\\') {
      i++;
      continue;
    }

    // Ignore anything that's not the start of a group
    if (char !== '(') {
      continue;
    }

    if (char === '(') {
      // Ignore non-capturing groups
      const isNonCapturing = pattern[i + 1] === '?' && pattern[i + 2] === ':';
      if (isNonCapturing) {
        i += 2; // skip next two chars (?:)
        continue;
      }

      // Find closing paren
      let j = i + 1;
      while (j < pattern.length) {
        const innerChar = pattern[j];

        // Ignore escaped characters
        if (innerChar === '\\') {
          j += 2;
          continue;
        }

        // Stop when we find the closing paren
        if (innerChar === ')') {
          break;
        }

        j++;
      }

      // Add capture group
      const content = pattern.substring(i + 1, j);
      groups.push({start: i, end: j + 1, content});

      // Continue scanning from the end of the group
      i = j;
    }
  }

  return groups;
}
